import * as L from 'leaflet';
import Track from './Track';
import { log as debugLog } from '@/ts/debug';

/**
 * Struktura bounds: [wiersz][kolumna] -> [ [pixelX, pixelY], [pixelX, pixelY], [pixelX, pixelY], [pixelX, pixelY] ]
 * Współrzędne świata w pikselach dla zoom = 0
 * 
 * Struktura values: [wiersz][kolumna] -> lista ID tras przechodzących przez kafel
 */
export type PolygonBounds = any[][][];
export type PolygonValues = any[][];

export class VisitedTilesLayer extends L.Layer {
  private canvas: HTMLCanvasElement | null = null;
  private bounds: PolygonBounds = [];
  private values: PolygonValues = [];
  private showAllTiles: boolean = false;
  private showVisitedTiles: boolean = false;
  private showOnlySelectedTracks: boolean = false;
  private selectedTracks: Track[] = [];
  private selectedTrackIds: Set<number> = new Set();

  // Color properties with defaults
  public visitedWithNeighborsFill: string = 'rgba(144, 238, 144, 0.5)';
  public visitedWithNeighborsStroke: string = '#006400';
  public visitedBoundaryFill: string = 'rgba(255, 100, 100, 0.5)';
  public visitedBoundaryStroke: string = '#8B0000';
  public unvisitedStroke: string = '#87CEEB';

  public setBounds(newBounds: PolygonBounds): void {
    this.bounds = newBounds;
    this.redraw();
  }

  public setValues(newValues: PolygonValues): void {
    this.values = newValues;
    this.redraw();
  }

  public setShowAllTiles(value: boolean): void {
    this.showAllTiles = value;
    this.redraw();
  }

  public setShowVisitedTiles(value: boolean): void {
    this.showVisitedTiles = value;
    this.redraw();
  }

  public setShowOnlySelectedTracks(value: boolean): void {
    this.showOnlySelectedTracks = value;
    this.redraw();
  }

  public setSelectedTracks(tracks: Track[]): void {
    this.selectedTracks = tracks;
    this.selectedTrackIds = new Set(tracks.map(track => track.id));
    if (this.showOnlySelectedTracks) {
      this.redraw();
    }
  }

  public setVisitedWithNeighborsFill(color: string): void {
    this.visitedWithNeighborsFill = color;
    this.redraw();
  }

  public setVisitedWithNeighborsStroke(color: string): void {
    this.visitedWithNeighborsStroke = color;
    this.redraw();
  }

  public setVisitedBoundaryFill(color: string): void {
    this.visitedBoundaryFill = color;
    this.redraw();
  }

  public setVisitedBoundaryStroke(color: string): void {
    this.visitedBoundaryStroke = color;
    this.redraw();
  }

  public setUnvisitedStroke(color: string): void {
    this.unvisitedStroke = color;
    this.redraw();
  }

  public getColors() {
    return {
      visitedWithNeighborsFill: this.visitedWithNeighborsFill,
      visitedWithNeighborsStroke: this.visitedWithNeighborsStroke,
      visitedBoundaryFill: this.visitedBoundaryFill,
      visitedBoundaryStroke: this.visitedBoundaryStroke,
      unvisitedStroke: this.unvisitedStroke
    };
  }

  onAdd(map: L.Map): this {
    this.canvas = L.DomUtil.create('canvas', 'leaflet-canvas-layer') as HTMLCanvasElement;
    
    // Dodanie do odpowiedniego panelu Leafleta
    // Umieść pane jako dziecko `tilePane` i obniż z-index tak, aby
    // poliliny oraz markery pozostały na wierzchu.
    const parentPane = (map as any).getPane ? (map as any).getPane('tilePane') : undefined;
    const pane = map.createPane('visitedTiles', parentPane);
    // Pozostaw niskie z-index (pod overlay/marker panes)
    pane.style.zIndex = '199';
    if (pane) pane.appendChild(this.canvas);
    if (this.canvas) {
      this.canvas.style.pointerEvents = 'none';
      this.canvas.style.zIndex = '0';
    }

    // Reagowanie na ruch i zoom
    map.on('move viewreset zoom', this.redraw, this);
    this.redraw();
    return this;
  }

  onRemove(map: L.Map): this {
    if (this.canvas) {
      L.DomUtil.remove(this.canvas);
    }
    map.off('move viewreset zoom', this.redraw, this);
    return this;
  }

  public redraw(): void {
    debugLog('redraw');
    const map = (this as any)._map as L.Map;
    if (!map || !this.canvas || this.bounds.length === 0 || this.values.length === 0) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    const size = map.getSize();
    this.canvas.width = size.x;
    this.canvas.height = size.y;

    const zoom = map.getZoom();
    const zoomScale = 2 ** zoom;

    // Współrzędne mapowe lewego górnego rogu ekranu
    const topLeftLatLng = map.containerPointToLatLng([0, 0]);
    const bottomRightLatLng = map.containerPointToLatLng([size.x, size.y]);
    
    // Projekcja do współrzędnych świata dla aktualnego zooma
    const topLeftProjected = map.project(topLeftLatLng, zoom);
    const bottomRightProjected = map.project(bottomRightLatLng, zoom);
    
    // Pozycja canvasu na warstwie
    const topLeft = map.latLngToLayerPoint(topLeftLatLng);
    L.DomUtil.setPosition(this.canvas, topLeft);

    // Granice widoku w współrzędnych świata dla aktualnego zooma
    const viewportMinX = Math.min(topLeftProjected.x, bottomRightProjected.x);
    const viewportMaxX = Math.max(topLeftProjected.x, bottomRightProjected.x);
    const viewportMinY = Math.min(topLeftProjected.y, bottomRightProjected.y);
    const viewportMaxY = Math.max(topLeftProjected.y, bottomRightProjected.y);

    ctx.clearRect(0, 0, size.x, size.y);

    // SCENARIO 1: Only showAllTiles (no visited tiles) - draw entire grid uniformly in unvisited color
    if (this.showAllTiles && !this.showVisitedTiles) {
      for (let i = 0; i < this.bounds.length; i++) {
        const row = this.bounds[i];
        if (!row) continue;
        
        for (let j = 0; j < row.length; j++) {
          const boundPoints = row[j];
          if (!boundPoints || boundPoints.length !== 4) continue;

          const points = boundPoints as [number, number][];

          // --- SZYBKI BBOX CHECK ---
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale;
            const y = points[p][1] * zoomScale;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }

          // Sprawdź czy bounding box przecina się z widokiem
          if (maxX < viewportMinX || minX > viewportMaxX || maxY < viewportMinY || minY > viewportMaxY) {
            continue;
          }

          // Draw all tiles (visited and unvisited) with unvisited color - uniform grid
          ctx.beginPath();
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale - topLeftProjected.x;
            const y = points[p][1] * zoomScale - topLeftProjected.y;
            if (p === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();

          ctx.strokeStyle = this.unvisitedStroke;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      return;
    }

    // SCENARIO 2: showVisitedTiles enabled (with or without showAllTiles)
    if (this.showVisitedTiles) {
      // PASS 1: Draw unvisited tiles if showAllTiles is also enabled
      if (this.showAllTiles) {
        for (let i = 0; i < this.bounds.length; i++) {
          const row = this.bounds[i];
          if (!row) continue;
          
          for (let j = 0; j < row.length; j++) {
            const boundPoints = row[j];
            if (!boundPoints || boundPoints.length !== 4) continue;

            const hasVisitedTracks = this.hasVisitedTracks(i, j);
            
            // Only draw unvisited tiles in this pass
            if (hasVisitedTracks) continue;

            const points = boundPoints as [number, number][];

            // --- SZYBKI BBOX CHECK ---
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            for (let p = 0; p < points.length; p++) {
              const x = points[p][0] * zoomScale;
              const y = points[p][1] * zoomScale;
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }

            // Sprawdź czy bounding box przecina się z widokiem
            if (maxX < viewportMinX || minX > viewportMaxX || maxY < viewportMinY || minY > viewportMaxY) {
              continue;
            }

            // Draw unvisited tile with light blue stroke, no fill
            ctx.beginPath();
            for (let p = 0; p < points.length; p++) {
              const x = points[p][0] * zoomScale - topLeftProjected.x;
              const y = points[p][1] * zoomScale - topLeftProjected.y;
              if (p === 0) {
                ctx.moveTo(x, y);
              } else {
                ctx.lineTo(x, y);
              }
            }
            ctx.closePath();

            ctx.strokeStyle = this.unvisitedStroke;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // PASS 2: Draw visited tiles with ALL neighbors (green - internal tiles)
      for (let i = 0; i < this.bounds.length; i++) {
        const row = this.bounds[i];
        if (!row) continue;
        
        for (let j = 0; j < row.length; j++) {
          const boundPoints = row[j];
          if (!boundPoints || boundPoints.length !== 4) continue;

          const hasVisitedTracks = this.hasVisitedTracks(i, j);
          
          // Only draw tiles with all neighbors visited
          if (!hasVisitedTracks) continue;
          
          const allNeighborsVisited = this.hasAllNeighborsVisited(i, j);
          if (!allNeighborsVisited) continue;

          const points = boundPoints as [number, number][];

          // --- SZYBKI BBOX CHECK ---
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale;
            const y = points[p][1] * zoomScale;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }

          // Sprawdź czy bounding box przecina się z widokiem
          if (maxX < viewportMinX || minX > viewportMaxX || maxY < viewportMinY || minY > viewportMaxY) {
            continue;
          }

          ctx.beginPath();
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale - topLeftProjected.x;
            const y = points[p][1] * zoomScale - topLeftProjected.y;
            if (p === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();

          // Green styling for fully surrounded visited tiles
          ctx.fillStyle = this.visitedWithNeighborsFill;
          ctx.fill();
          ctx.strokeStyle = this.visitedWithNeighborsStroke;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      // PASS 3: Draw visited boundary tiles (red - visited but not all neighbors visited)
      for (let i = 0; i < this.bounds.length; i++) {
        const row = this.bounds[i];
        if (!row) continue;
        
        for (let j = 0; j < row.length; j++) {
          const boundPoints = row[j];
          if (!boundPoints || boundPoints.length !== 4) continue;

          const hasVisitedTracks = this.hasVisitedTracks(i, j);
          
          // Only draw visited tiles that DON'T have all neighbors
          if (!hasVisitedTracks) continue;
          
          const allNeighborsVisited = this.hasAllNeighborsVisited(i, j);
          if (allNeighborsVisited) continue; // Skip if already drawn in pass 2

          const points = boundPoints as [number, number][];

          // --- SZYBKI BBOX CHECK ---
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale;
            const y = points[p][1] * zoomScale;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }

          // Sprawdź czy bounding box przecina się z widokiem
          if (maxX < viewportMinX || minX > viewportMaxX || maxY < viewportMinY || minY > viewportMaxY) {
            continue;
          }

          ctx.beginPath();
          for (let p = 0; p < points.length; p++) {
            const x = points[p][0] * zoomScale - topLeftProjected.x;
            const y = points[p][1] * zoomScale - topLeftProjected.y;
            if (p === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
          ctx.closePath();

          // Red styling for boundary tiles
          ctx.fillStyle = this.visitedBoundaryFill;
          ctx.fill();
          ctx.strokeStyle = this.visitedBoundaryStroke;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
    }
  }

  private hasAllNeighborsVisited(i: number, j: number): boolean {
    // Check top (i-1), bottom (i+1), left (i, j-1), right (i, j+1)
    const neighbors = [
      { x: i - 1, y: j },      // top
      { x: i + 1, y: j },      // bottom
      { x: i, y: j - 1 },      // left
      { x: i, y: j + 1 }       // right
    ];

    for (const neighbor of neighbors) {
      const { x, y } = neighbor;
      
      // Check bounds
      if (x < 0 || x >= this.bounds.length || y < 0 || y >= this.bounds[x].length) {
        return false;
      }

      if (!this.hasVisitedTracks(x, y)) {
        return false;
      }
    }

    return true;
  }

  private hasVisitedTracks(i: number, j: number): boolean {
    const trackIds = this.values[i]?.[j];
    if (this.showOnlySelectedTracks) {
      return Array.isArray(trackIds)
        && trackIds.some(trackId => this.selectedTrackIds.has(trackId));
    }
    return (Array.isArray(trackIds) && trackIds.length > 0)
      || (typeof trackIds === 'number' && trackIds > 0);
  }
}